import React, { useEffect, useState } from "react";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Play, Camera, CheckCircle2, AlertTriangle, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import PhotoUploader from "@/components/PhotoUploader";

const CHECKLIST = ["Bed made", "Trash removed", "Surfaces dusted", "Floor vacuumed", "Floor mopped", "Bathroom cleaned", "Kitchen wiped", "Mirrors cleaned"];

export default function CleanerDashboard() {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  const [activeId, setActiveId] = useState(null);

  async function load() {
    const { data } = await http.get("/appointments");
    setAppts(data);
  }
  useEffect(() => { load(); }, []);

  const active = appts.find((a) => a.id === activeId);

  async function start(id) {
    await http.post(`/appointments/${id}/status`, { status: "in_progress" });
    toast.success("Job started");
    setActiveId(id);
    load();
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <Badge variant="outline">Cleaner</Badge>
      <h1 className="font-display text-3xl font-bold text-primary mt-2" data-testid="cleaner-dashboard-title">Today, {user?.name?.split(" ")[0]}</h1>
      <p className="text-muted-foreground text-sm mt-1">{appts.length} job{appts.length === 1 ? "" : "s"} on your schedule.</p>

      <div className="space-y-3 mt-6">
        {appts.length === 0 && <Card><CardContent className="p-8 text-center text-muted-foreground">No jobs assigned yet. Check back soon.</CardContent></Card>}
        {appts.map((a) => (
          <Card key={a.id} className="border-border/60" data-testid={`job-card-${a.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display font-semibold text-primary text-lg">{a.property?.name || "Job"}</div>
                  <div className="text-sm text-muted-foreground">{a.property?.address}</div>
                  <div className="text-xs text-muted-foreground mt-1">Building: {a.property?.building || "—"} · Room: {a.property?.room_number || "—"}</div>
                  <div className="text-xs text-muted-foreground mt-1">Parent: {a.parent_name} · Student: {a.student?.name}</div>
                </div>
                <Badge className={a.status === "completed" ? "bg-secondary/10 text-secondary" : "bg-primary/10 text-primary"}>{a.status}</Badge>
              </div>
              {a.special_instructions && <div className="bg-muted/40 rounded-lg p-2 text-xs mt-3"><b>Notes:</b> {a.special_instructions}</div>}
              <div className="mt-4 flex flex-wrap gap-2">
                {a.status === "scheduled" && <Button size="sm" onClick={() => start(a.id)} className="bg-primary hover:bg-primary/90" data-testid={`start-job-${a.id}`}><Play size={14} className="mr-1" /> Start job</Button>}
                {a.status === "in_progress" && (
                  <ReportDialog appt={a} onSubmitted={load} />
                )}
                {a.status === "completed" && <span className="text-xs text-secondary flex items-center gap-1"><CheckCircle2 size={14} /> Submitted</span>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function ReportDialog({ appt, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    arrival_time: "", completion_time: "",
    cleanliness_score: 8, organization_score: 7,
    trash_removed: false, laundry_observed: false,
    kitchen_condition: "Clean", bathroom_condition: "Clean", odor: "Neutral",
    damages: "", maintenance_issues: "", comments: "", recommendations: "",
    before_photos: [], after_photos: [],
    bedroom_photos: [], bathroom_photos: [], kitchen_photos: [], living_room_photos: [], maintenance_photos: [],
    cleaner_notes: "",
    checklist: {},
  });

  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit() {
    setBusy(true);
    try {
      await http.post("/reports", {
        appointment_id: appt.id,
        arrival_time: form.arrival_time || new Date().toISOString(),
        completion_time: form.completion_time || new Date().toISOString(),
        cleanliness_score: Number(form.cleanliness_score),
        organization_score: Number(form.organization_score),
        trash_removed: form.trash_removed,
        laundry_observed: form.laundry_observed,
        kitchen_condition: form.kitchen_condition,
        bathroom_condition: form.bathroom_condition,
        odor: form.odor,
        damages: form.damages,
        maintenance_issues: form.maintenance_issues,
        comments: form.comments,
        recommendations: form.recommendations,
        before_photos: form.before_photos,
        after_photos: form.after_photos,
        bedroom_photos: form.bedroom_photos,
        bathroom_photos: form.bathroom_photos,
        kitchen_photos: form.kitchen_photos,
        living_room_photos: form.living_room_photos,
        maintenance_photos: form.maintenance_photos,
        cleaner_notes: form.cleaner_notes,
      });
      toast.success("Report submitted!");
      setOpen(false);
      onSubmitted();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-secondary hover:bg-secondary/90" data-testid={`submit-report-btn-${appt.id}`}><ClipboardList size={14} className="mr-1" /> Submit report</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Cleaning Report — {appt.property?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Cleanliness ({form.cleanliness_score}/10)</Label><Slider value={[form.cleanliness_score]} onValueChange={(v) => up("cleanliness_score", v[0])} min={1} max={10} step={1} /></div>
            <div><Label className="text-xs">Organization ({form.organization_score}/10)</Label><Slider value={[form.organization_score]} onValueChange={(v) => up("organization_score", v[0])} min={1} max={10} step={1} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.trash_removed} onCheckedChange={(v) => up("trash_removed", !!v)} data-testid="report-trash" /> Trash removed</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.laundry_observed} onCheckedChange={(v) => up("laundry_observed", !!v)} data-testid="report-laundry" /> Laundry observed</label>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Kitchen</Label><Input value={form.kitchen_condition} onChange={(e) => up("kitchen_condition", e.target.value)} /></div>
            <div><Label className="text-xs">Bathroom</Label><Input value={form.bathroom_condition} onChange={(e) => up("bathroom_condition", e.target.value)} /></div>
            <div><Label className="text-xs">Odor</Label><Input value={form.odor} onChange={(e) => up("odor", e.target.value)} /></div>
          </div>
          <PhotoUploader value={form.bedroom_photos} onChange={(v) => up("bedroom_photos", v)} label="Bedroom photos" testId="report-bedroom-photos" />
          <PhotoUploader value={form.bathroom_photos} onChange={(v) => up("bathroom_photos", v)} label="Bathroom photos" testId="report-bathroom-photos" />
          <PhotoUploader value={form.kitchen_photos} onChange={(v) => up("kitchen_photos", v)} label="Kitchen photos" testId="report-kitchen-photos" />
          <PhotoUploader value={form.living_room_photos} onChange={(v) => up("living_room_photos", v)} label="Living room photos (optional)" testId="report-living-room-photos" />
          <PhotoUploader value={form.maintenance_photos} onChange={(v) => up("maintenance_photos", v)} label="Maintenance issue photos" accent="secondary" testId="report-maintenance-photos" />
          <div><Label className="text-xs">Damages</Label><Textarea rows={2} value={form.damages} onChange={(e) => up("damages", e.target.value)} /></div>
          <div><Label className="text-xs">Maintenance issues</Label><Textarea rows={2} value={form.maintenance_issues} onChange={(e) => up("maintenance_issues", e.target.value)} data-testid="report-maintenance" /></div>
          <div><Label className="text-xs">Summary comments</Label><Textarea rows={3} value={form.comments} onChange={(e) => up("comments", e.target.value)} data-testid="report-comments" /></div>
          <div><Label className="text-xs">Recommendations</Label><Textarea rows={2} value={form.recommendations} onChange={(e) => up("recommendations", e.target.value)} /></div>
          <Button onClick={submit} disabled={busy} className="w-full bg-primary hover:bg-primary/90" data-testid="report-submit-final"><Camera size={14} className="mr-1" /> {busy ? "Submitting..." : "Submit report"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
